import { createContext, useContext, useState, useEffect, useMemo, useCallback, useRef } from "react";
import axios from "axios";
import { useAuth } from "./AuthContext";
import { formatDateForBackend } from "../features/dateFormaters";
import { handleApiError } from "../features/errorHandler";
import { CREDIT_VALUE_EUR, PUBLIC_PRODUCT_CATALOG_URL } from "../features/UrlHelper";

const CartContext = createContext(null);

export const CART_STORAGE_KEY = "_ks_client_state";
export const CART_PAYMENT_METHOD_KEY = "_ks_client_pref";
const CART_CIPHER_KEY = "Triferis_CartSecurity_Salt2026!";

// Bezpečné zašifrovanie košíka bez externých knižníc (TextEncoder + XOR + Base64)
const encryptCartStorage = (data) => {
  try {
    const jsonStr = JSON.stringify(data);
    const utf8Bytes = new TextEncoder().encode(jsonStr);
    const keyBytes = new TextEncoder().encode(CART_CIPHER_KEY);
    const encrypted = new Uint8Array(utf8Bytes.length);
    for (let i = 0; i < utf8Bytes.length; i++) {
      encrypted[i] = utf8Bytes[i] ^ keyBytes[i % keyBytes.length];
    }
    let binary = "";
    for (let i = 0; i < encrypted.length; i++) {
      binary += String.fromCharCode(encrypted[i]);
    }
    return btoa(binary);
  } catch (err) {
    return "";
  }
};

// Dešifrovanie a overenie integrity košíka z localStorage
const decryptCartStorage = (ciphertext) => {
  if (!ciphertext) return [];
  try {
    // Spätná kompatibilita pre doterajší čistý JSON formát
    if (ciphertext.startsWith("[") || ciphertext.startsWith("{")) {
      const parsed = JSON.parse(ciphertext);
      return Array.isArray(parsed) ? parsed : [];
    }

    const binary = atob(ciphertext);
    const encrypted = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      encrypted[i] = binary.charCodeAt(i);
    }
    const keyBytes = new TextEncoder().encode(CART_CIPHER_KEY);
    const decrypted = new Uint8Array(encrypted.length);
    for (let i = 0; i < encrypted.length; i++) {
      decrypted[i] = encrypted[i] ^ keyBytes[i % keyBytes.length];
    }
    const jsonStr = new TextDecoder().decode(decrypted);
    const parsed = JSON.parse(jsonStr);
    return Array.isArray(parsed) ? parsed : [];
  } catch (err) {
    // Ak niekto v DevTools čokoľvek prepísal, dešifrovanie zlyhá a bezpečne resetuje košík
    console.warn("⚠️ Záznam košíka v localStorage bol poškodený alebo upravený. Košík bol vyresetovaný.");
    return [];
  }
};

export function CartProvider({ children }) {
  const { isLoggedIn, userId, userEmail, userName, firstName, lastName, openLoginModal } = useAuth();

  // Zoznam flat položiek načítaný z localStorage
  const [items, setItems] = useState(() => {
    try {
      const saved =
        localStorage.getItem(CART_STORAGE_KEY);
      if (saved) {
        return decryptCartStorage(saved);
      }
    } catch (_) { }
    return [];
  });

  const [paymentMethod, setPaymentMethodState] = useState(() => {
    try {
      const saved =
        localStorage.getItem(CART_PAYMENT_METHOD_KEY);
      return saved === "transfer" ? "transfer" : "";
    } catch (_) {
      return "";
    }
  });

  const [createdAt, setCreatedAt] = useState(() => new Date().toISOString());
  const [updatedAt, setUpdatedAt] = useState(() => new Date().toISOString());
  const [appliedCredits, setAppliedCreditsState] = useState(0);

  const setAppliedCredits = useCallback((val) => {
    setAppliedCreditsState((prev) => {
      const num = parseInt(val || 0, 10);
      return isNaN(num) || num < 0 ? 0 : num;
    });
  }, []);

  // Sledovanie predchádzajúceho stavu prihlásenia pre vyčistenie košíka pri odhlásení
  const prevIsLoggedInRef = useRef(isLoggedIn);

  useEffect(() => {
    // Ak bol používateľ predtým prihlásený a teraz sa odhlásil (explicit logout)
    if (prevIsLoggedInRef.current && !isLoggedIn) {
      setItems([]);
      setAppliedCredits(0);
      try {
        localStorage.removeItem(CART_STORAGE_KEY);
      } catch (_) { }
    }
    prevIsLoggedInRef.current = isLoggedIn;
  }, [isLoggedIn]);

  // Automatické ukladanie do localStorage pri akejkoľvek zmene (zašifrované)
  useEffect(() => {
    try {
      if (items.length > 0) {
        localStorage.setItem(CART_STORAGE_KEY, encryptCartStorage(items));
      } else {
        localStorage.removeItem(CART_STORAGE_KEY);
      }
    } catch (_) { }
  }, [items]);

  useEffect(() => {
    try {
      localStorage.setItem(CART_PAYMENT_METHOD_KEY, paymentMethod);
    } catch (_) { }
  }, [paymentMethod]);

  const customerName = useMemo(() => {
    return [firstName, lastName].filter(Boolean).join(" ") || userName || "Používateľ";
  }, [firstName, lastName, userName]);

  // Pomocná funkcia na vytvorenie unikátneho kľúča termínu
  const getScheduleKey = useCallback((sch, idx = 0) => {
    if (!sch) return `sch_${idx}`;
    return sch.id ? `sch_${sch.id}` : `sch_${sch.day}_${sch.time_from}_${sch.time_to}`;
  }, []);

  // Pomocná funkcia na zistenie ceny termínu / produktu
  const getSchedulePrice = (product, schedule) => {
    if (schedule && typeof schedule.schedule_price === "number") {
      return schedule.schedule_price;
    }
    if (schedule && typeof schedule.price === "number") {
      return schedule.price;
    }
    if (product.final_price !== undefined && product.final_price !== null) {
      return parseFloat(product.final_price);
    }
    if (
      product.discount_price !== null &&
      product.discount_price !== undefined &&
      product.discount_price !== ""
    ) {
      return parseFloat(product.discount_price);
    }
    return parseFloat(product.price || 0);
  };

  // Helper na vytvorenie jedného flat itemu: (Produkt + Termín + Osoba)
  const createFlatItem = (
    product,
    schedule = null,
    person = null,
    schIdx = 0,
    explicitTermKey = null
  ) => {
    const schedules = Array.isArray(product.availableSchedules)
      ? product.availableSchedules
      : Array.isArray(product.schedules)
        ? product.schedules
        : [];

    const activeSchedule =
      schedule !== undefined && schedule !== null
        ? schedule
        : schedules.find((s) => !s.is_full) || (schedules.length > 0 ? schedules[0] : null);

    const termKey =
      explicitTermKey ||
      product.termKey ||
      (activeSchedule ? getScheduleKey(activeSchedule, schIdx) : `sch_${product.id || product.productId || 0}_${schIdx}`);

    const price = getSchedulePrice(product, activeSchedule);
    const originalPrice = parseFloat(product.original_price ?? product.price ?? price);
    const uniqueId = `item_${product.id || product.productId}_${termKey}_${Date.now()}_${Math.random()
      .toString(36)
      .substring(2, 6)}`;

    return {
      cartItemId: uniqueId,
      itemId: uniqueId,
      productId: product.id || product.productId,
      productName: product.name || product.productName,
      productDescription: product.description || product.productDescription || "",
      school_id: product.school_id || null,
      school_name: product.school_name || null,
      school_address: product.school_address || null,
      school_city: product.school_city || null,
      photo:
        product.photo ||
        product.photo_1 ||
        product.photo_2 ||
        product.photo_3 ||
        product.photo_4 ||
        null,
      unitPrice: price,
      price,
      originalPrice,
      hasDiscount:
        product.has_discount !== undefined
          ? product.has_discount
          : price < originalPrice,
      cancellation_credits: product.cancellation_credits || 0,
      date_from: formatDateForBackend(product.date_from),
      date_to: formatDateForBackend(product.date_to),
      schedule: activeSchedule,
      scheduleId: activeSchedule?.id || null,
      day: activeSchedule?.day || null,
      timeFrom: activeSchedule?.time_from || null,
      timeTo: activeSchedule?.time_to || null,
      school_id: product.school_id || null,
      school_name: product.school_name || null,
      school_address: product.school_address || null,
      school_city: product.school_city || null,
      trainer: activeSchedule?.trainer || null,
      termKey,
      availableSchedules: schedules,
      person: person
        ? {
          id: person.id || null,
          firstName: person.firstName || person.first_name || "",
          lastName: person.lastName || person.last_name || "",
          birthDate: formatDateForBackend(person.birthDate || person.birth_date),
          phone: person.phone || null,
          school_id: person.school_id || person.schoolId || null,
          school_name: person.school_name || person.schoolName || null,
          class_grade: person.class_grade || person.classGrade || null,
          class_letter: person.class_letter || person.classLetter || null,
          note: person.note || null,
        }
        : null,
      addedAt: new Date().toISOString(),
    };
  };

  // ─── 1. Zmena platobnej metódy ──────────────────────────────────
  const setPaymentMethod = (method) => {
    setPaymentMethodState(method);
    setUpdatedAt(new Date().toISOString());
  };

  // ─── 2. Pridanie produktu do košíka (vytvorí flat item) ─────────
  const addCourseToCart = (product, schedule = null, person = null) => {
    if (!userId) {
      if (openLoginModal) openLoginModal();
      return null;
    }

    const schedules = Array.isArray(product.availableSchedules)
      ? product.availableSchedules
      : Array.isArray(product.schedules)
        ? product.schedules
        : [];

    // Zvoľme buď explicitne zadaný rozvrh (ak nie je plný), alebo prvý voľný rozvrh
    const nonFullSchedule = schedules.find((s) => !s.is_full);
    const activeSchedule = schedule ? schedule : nonFullSchedule;

    // Ak nie je k dispozícii žiadny voľný termín alebo je zadaný termín plný, nepridáme nič
    if (!activeSchedule || activeSchedule.is_full) {
      return null;
    }

    const termKey = activeSchedule
      ? getScheduleKey(activeSchedule, 0)
      : `sch_${product.id || product.productId || 0}_0`;

    // Ak už v košíku existuje položka pre tento produkt a termín bez priradenej osoby
    const existing = items.find(
      (item) => item.productId === (product.id || product.productId) && item.termKey === termKey
    );

    if (existing && !person) {
      return existing;
    }

    const newItem = createFlatItem(product, activeSchedule, person, 0, termKey);
    setItems((prev) => [...prev, newItem]);
    setUpdatedAt(new Date().toISOString());
    return newItem;
  };

  // ─── 3. Toggle celého krúžku v košíku (pridať / odobrať) ──────────
  const toggleCourseInCart = (product) => {
    if (!userId) {
      if (openLoginModal) openLoginModal();
      return false;
    }

    const productId = product.id || product.productId;
    const exists = items.some((item) => item.productId === productId);
    if (exists) {
      removeFromCart(productId);
      return false;
    } else {
      if (product.is_full) {
        return false;
      }
      const added = addCourseToCart(product);
      return Boolean(added);
    }
  };

  // ─── 4. Toggle termínu v rámci krúžku ─────────────────────────────
  const toggleTermInCourse = (productId, schedule, schIdx = 0) => {
    if (!userId) {
      if (openLoginModal) openLoginModal();
      return;
    }

    const termKey = getScheduleKey(schedule, schIdx);

    setItems((prev) => {
      const itemsForThisTerm = prev.filter(
        (i) => i.productId === productId && i.termKey === termKey
      );

      if (itemsForThisTerm.length > 0) {
        return prev.filter(
          (i) => !(i.productId === productId && i.termKey === termKey)
        );
      } else {
        // Ak je termín plne obsadený, nedovolíme ho pridať
        if (schedule?.is_full) {
          return prev;
        }

        const productRef = prev.find((i) => i.productId === productId);
        if (!productRef) return prev;

        const newItem = createFlatItem(productRef, schedule, null, schIdx, termKey);
        return [...prev, newItem];
      }
    });
    setUpdatedAt(new Date().toISOString());
  };

  // ─── 5. Priradiť alebo pridať osobu k termínu produktu ────────────
  const addPersonToTerm = (productId, termKey, personData) => {
    if (!userId) {
      if (openLoginModal) openLoginModal();
      return;
    }

    const normalizedPerson = {
      id: personData.id || null,
      firstName: personData.firstName || personData.first_name || "",
      lastName: personData.lastName || personData.last_name || "",
      birthDate: formatDateForBackend(personData.birthDate || personData.birth_date),
      phone: personData.phone || null,
      school_id: personData.school_id ? parseInt(personData.school_id, 10) : (personData.schoolId ? parseInt(personData.schoolId, 10) : null),
      school_name: personData.school_name || personData.schoolName || null,
      class_grade: personData.class_grade || personData.classGrade || null,
      class_letter: personData.class_letter || personData.classLetter || null,
      note: personData.note || null,
    };

    setItems((prev) => {
      // 1. Skontrolujeme, či už táto konkrétna osoba na tomto termíne je
      const existingPersonItem = prev.find(
        (i) =>
          i.productId === productId &&
          i.termKey === termKey &&
          i.person &&
          ((normalizedPerson.id && i.person.id === normalizedPerson.id) ||
            (i.person.firstName?.trim().toLowerCase() ===
              normalizedPerson.firstName?.trim().toLowerCase() &&
              i.person.lastName?.trim().toLowerCase() ===
              normalizedPerson.lastName?.trim().toLowerCase()))
      );

      if (existingPersonItem) {
        return prev.map((i) =>
          i.cartItemId === existingPersonItem.cartItemId
            ? { ...i, person: normalizedPerson }
            : i
        );
      }

      // 2. Skontrolujeme, či je na danom termíne prázdna položka bez priradenej osoby (person === null)
      const emptyItem = prev.find(
        (i) => i.productId === productId && i.termKey === termKey && !i.person
      );

      if (emptyItem) {
        return prev.map((i) =>
          i.cartItemId === emptyItem.cartItemId
            ? { ...i, person: normalizedPerson }
            : i
        );
      }

      // 3. Ďalšie dieťa na ten istý termín = ďalší flat item!
      // Musí ísť o presnú zhodu termKey – inak by sa prevzal rozvrh (scheduleId/day) z iného termínu produktu.
      const sampleItem = prev.find((i) => i.productId === productId && i.termKey === termKey);

      if (!sampleItem) return prev;

      const newItem = createFlatItem(
        sampleItem,
        sampleItem.schedule,
        normalizedPerson,
        0,
        termKey
      );
      return [...prev, newItem];
    });
    setUpdatedAt(new Date().toISOString());
  };

  // ─── 6. Odstrániť konkrétne dieťa z termínu ───────────────────────
  const removePersonFromTerm = (productId, termKey, personIdOrIndex) => {
    if (!userId) return;

    setItems((prev) => {
      const matchingItems = prev.filter(
        (i) => i.productId === productId && i.termKey === termKey
      );

      const targetItem = matchingItems.find((i, idx) => {
        if (i.person?.id !== undefined && i.person?.id !== null) {
          return i.person.id === personIdOrIndex;
        }
        return idx === personIdOrIndex;
      });

      if (!targetItem) return prev;

      if (matchingItems.length > 1) {
        return prev.filter((i) => i.cartItemId !== targetItem.cartItemId);
      } else {
        return prev.map((i) =>
          i.cartItemId === targetItem.cartItemId ? { ...i, person: null } : i
        );
      }
    });
    setUpdatedAt(new Date().toISOString());
  };

  // ─── 7. Zmazať flat item priamo podľa cartItemId ──────────────────
  const removeCartItem = (cartItemId) => {
    setItems((prev) => prev.filter((i) => i.cartItemId !== cartItemId));
    setUpdatedAt(new Date().toISOString());
  };

  // ─── 8. Aktualizovať osobu priamo na konkrétnom flat iteme ────────
  const updateItemPerson = (cartItemId, personData) => {
    if (!userId) return;
    const normalizedPerson = {
      id: personData.id || null,
      firstName: personData.firstName || personData.first_name || "",
      lastName: personData.lastName || personData.last_name || "",
      birthDate: formatDateForBackend(personData.birthDate || personData.birth_date),
      phone: personData.phone || null,
      school_id: personData.school_id ? parseInt(personData.school_id, 10) : (personData.schoolId ? parseInt(personData.schoolId, 10) : null),
      school_name: personData.school_name || personData.schoolName || null,
      class_grade: personData.class_grade || personData.classGrade || null,
      class_letter: personData.class_letter || personData.classLetter || null,
      note: personData.note || null,
    };
    setItems((prev) =>
      prev.map((i) =>
        i.cartItemId === cartItemId ? { ...i, person: normalizedPerson } : i
      )
    );
    setUpdatedAt(new Date().toISOString());
  };

  // ─── 9. Odstrániť celý krúžok z košíka (všetky jeho flat položky) ──
  const removeFromCart = (productId) => {
    setItems((prev) => prev.filter((item) => item.productId !== productId));
    setUpdatedAt(new Date().toISOString());
  };

  // ─── 10. Vyprázdniť celý košík ────────────────────────────────────
  const clearCart = () => {
    setItems([]);
    setAppliedCredits(0);
    setUpdatedAt(new Date().toISOString());
    try {
      localStorage.removeItem(CART_STORAGE_KEY);
    } catch (_) { }
  };

  // ─── 10b. Obnoviť ceny položiek podľa aktuálneho katalógu ─────────
  // Cena závisí od počtu zostávajúcich lekcií, takže sa v čase mení (napr. po začiatku dnešnej lekcie).
  // Vracia zoznam zmenených a nedostupných položiek; failed = katalóg sa nepodarilo načítať.
  const refreshCartPrices = async () => {
    const result = { changed: [], unavailable: [], failed: false };
    if (items.length === 0) return result;

    let catalog;
    try {
      const res = await axios.get(PUBLIC_PRODUCT_CATALOG_URL);
      catalog = Array.isArray(res.data) ? res.data : [];
    } catch (e) {
      handleApiError(e, "Nepodarilo sa overiť aktuálne ceny v košíku.");
      result.failed = true;
      return result;
    }

    const productsById = new Map(catalog.map((p) => [String(p.id), p]));
    const updates = new Map();

    items.forEach((item) => {
      const product = productsById.get(String(item.productId));
      const scheduleId = item.scheduleId || item.schedule?.id;
      const schedule = product?.schedules?.find((s) => String(s.id) === String(scheduleId));

      if (!product || !schedule) {
        result.unavailable.push({ cartItemId: item.cartItemId, productName: item.productName });
        return;
      }

      const fresh = createFlatItem(product, schedule, item.person, 0, item.termKey);
      const oldPrice = parseFloat(item.price ?? item.unitPrice ?? 0);
      if (Math.abs(oldPrice - fresh.price) > 0.005) {
        result.changed.push({
          cartItemId: item.cartItemId,
          productName: item.productName,
          oldPrice,
          newPrice: fresh.price,
        });
      }

      updates.set(item.cartItemId, {
        unitPrice: fresh.unitPrice,
        price: fresh.price,
        originalPrice: fresh.originalPrice,
        hasDiscount: fresh.hasDiscount,
        cancellation_credits: fresh.cancellation_credits,
        date_from: fresh.date_from,
        date_to: fresh.date_to,
        schedule: fresh.schedule,
        day: fresh.day,
        timeFrom: fresh.timeFrom,
        timeTo: fresh.timeTo,
        availableSchedules: fresh.availableSchedules,
      });
    });

    if (updates.size > 0) {
      setItems((prev) =>
        prev.map((i) => (updates.has(i.cartItemId) ? { ...i, ...updates.get(i.cartItemId) } : i))
      );
      if (result.changed.length > 0) {
        setUpdatedAt(new Date().toISOString());
      }
    }

    return result;
  };

  // ─── 11. Overiť či je krúžok v košíku ─────────────────────────────
  const isCourseInCart = (productId) => {
    return items.some((item) => String(item.productId) === String(productId));
  };

  // ─── 12. Agregácie & Sumáre ───────────────────────────────────────
  const activeItems = items;

  // Počet unikátnych krúžkov v košíku
  const cartCount = new Set(activeItems.map((i) => i.productId)).size;

  // Celkový počet položiek (prihlášok) v košíku
  const totalItemsCount = activeItems.length;

  // Celkový počet detí s priradeným menom (alebo počet položiek)
  const totalChildrenCount = activeItems.filter((i) => i.person).length || activeItems.length;

  // Celkový počet unikátnych zvolených termínov
  const totalTermsCount = new Set(activeItems.map((i) => `${i.productId}_${i.termKey}`)).size;

  // Počet termínov bez priradeného dieťaťa
  const termsWithoutChildren = activeItems.filter((item) => !item.person).length;

  // Celková suma položiek (bez zľavy kreditov)
  const rawCartTotal = activeItems.reduce(
    (acc, item) => acc + (parseFloat(item.price ?? item.unitPrice) || 0),
    0
  );

  // Celková suma po uplatnení kreditov (1 kredit = CREDIT_VALUE_EUR €)
  const creditsDiscountEur = appliedCredits * CREDIT_VALUE_EUR;
  const cartTotal = Math.max(0, parseFloat((rawCartTotal - creditsDiscountEur).toFixed(2)));

  // Variabilný symbol aj číslo objednávky sú generované autoritou na backende pri vytvorení objednávky
  const variableSymbol = "";

  // ─── 13. Kompletný Cart (Hlavička + Flat Items) ───────────────────
  const cart = useMemo(() => {
    const summary = {
      totalItems: totalItemsCount,
      cartCount,
      totalTermsCount,
      totalChildrenCount,
      termsWithoutChildren,
      rawCartTotal,
      appliedCredits,
      creditsDiscountEur,
      cartTotal,
    };

    if (!userId) {
      return {
        variableSymbol: "",
        userId: null,
        userEmail: "",
        customerName: "",
        paymentMethod,
        currency: "EUR",
        totalAmount: 0,
        status: "draft",
        createdAt: null,
        updatedAt: null,
        summary,
        items: [],
      };
    }

    return {
      variableSymbol: "",
      userId,
      userEmail: userEmail || "",
      customerName,
      paymentMethod,
      currency: "EUR",
      totalAmount: cartTotal,
      status: "draft",
      createdAt,
      updatedAt,
      summary,
      items: activeItems,
    };
  }, [
    userId,
    userEmail,
    customerName,
    paymentMethod,
    cartTotal,
    createdAt,
    updatedAt,
    totalItemsCount,
    cartCount,
    totalTermsCount,
    totalChildrenCount,
    termsWithoutChildren,
    activeItems,
  ]);

  // ─── 14. Výstupný payload pripravený pre BE ────────────────────────
  const getOrderPayload = useCallback(
    (paymentMethodOverride = null) => {
      const method = paymentMethodOverride || paymentMethod;

      return {
        variableSymbol: null,
        userId: userId || null,
        userEmail: userEmail || "",
        customerName: customerName || "Používateľ",
        paymentMethod: method,
        totalAmount: cartTotal,
        creditsUsed: appliedCredits,
        rawCartTotal,
        currency: "EUR",
        createdAt: new Date().toISOString(),
        status: method === "card" || cartTotal === 0 ? "paid" : "pending",
        items: activeItems.map((item, index) => ({
          itemId: item.cartItemId || `item_${index + 1}`,
          cartItemId: item.cartItemId,
          productId: item.productId,
          productName: item.productName,
          productDescription: item.productDescription || "",
          photo: item.photo || null,
          scheduleId: item.scheduleId || item.schedule?.id || null,
          day: item.day || item.schedule?.day || null,
          timeFrom: item.timeFrom || item.schedule?.time_from || null,
          timeTo: item.timeTo || item.schedule?.time_to || null,
          school_id: item.school_id || null,
          school_name: item.school_name || null,
          school_address: item.school_address || null,
          trainer: item.trainer || item.schedule?.trainer || null,
          termKey: item.termKey,
          unitPrice: item.price ?? item.unitPrice ?? 0,
          price: item.price ?? item.unitPrice ?? 0,
          cancellation_credits: item.cancellation_credits || 0,
          date_from: formatDateForBackend(item.date_from || item.dateFrom),
          date_to: formatDateForBackend(item.date_to || item.dateTo),
          person: item.person
            ? {
              id: item.person.id || null,
              firstName: item.person.firstName || item.person.first_name || "",
              lastName: item.person.lastName || item.person.last_name || "",
              birthDate: formatDateForBackend(item.person.birthDate || item.person.birth_date),
              phone: item.person.phone || null,
              school_id: item.person.school_id || item.person.schoolId || null,
              school_name: item.person.school_name || item.person.schoolName || null,
              class_grade: item.person.class_grade || item.person.classGrade || null,
              class_letter: item.person.class_letter || item.person.classLetter || null,
              note: item.person.note || null,
            }
            : null,
        })),
        summary: {
          totalItems: totalItemsCount,
          cartCount,
          totalTermsCount,
          totalChildrenCount,
          termsWithoutChildren,
          rawCartTotal,
          appliedCredits,
          cartTotal,
        },
      };
    },
    [
      paymentMethod,
      userId,
      userEmail,
      customerName,
      cartTotal,
      rawCartTotal,
      appliedCredits,
      activeItems,
      totalItemsCount,
      cartCount,
      totalTermsCount,
      totalChildrenCount,
      termsWithoutChildren,
    ]
  );

  return (
    <CartContext.Provider
      value={{
        // Kompletný objekt Hlavička + Flat Items
        cart,
        // Zoznam položiek
        cartItems: activeItems,
        items: activeItems,
        flattenedItems: activeItems,
        // Hlavičkové polia
        variableSymbol,
        paymentMethod,
        setPaymentMethod,
        // Sumáre & Agregácie
        cartTotal,
        rawCartTotal,
        appliedCredits,
        setAppliedCredits,
        totalAmount: cartTotal,
        cartCount,
        totalItemsCount,
        totalTermsCount,
        totalChildrenCount,
        termsWithoutChildren,
        summary: cart.summary,
        // Akcie
        addCourseToCart,
        toggleCourseInCart,
        toggleTermInCourse,
        addPersonToTerm,
        removePersonFromTerm,
        updateItemPerson,
        removeCartItem,
        removeFromCart,
        clearCart,
        refreshCartPrices,
        isCourseInCart,
        getScheduleKey,
        getOrderPayload,
        getFlattenedItems: () => activeItems,
        // Aliases
        addToCart: addCourseToCart,
        isProductInCart: isCourseInCart,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart musí byť použitý v rámci CartProvider");
  }
  return context;
}
